import { useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import type { MasterLookupRecord } from "@/types";

/** Units the converter understands, in the order a textile user thinks of them */
const CONVERTIBLE = ["MTR", "YDS", "ROLL", "THAN", "KG", "CONE", "BAG", "PCS", "DOZ", "PAIR"];

interface Result {
  ok: boolean;
  quantity?: number;
  how?: string;
  reason?: string;
}

/** Works out a quantity of a saved product in another unit, using its own GSM, width, roll length and cone weight */
export function UnitConverter({ productId, defaultUnit, units }: { productId: string; defaultUnit: string; units: MasterLookupRecord[] }) {
  const [qty, setQty] = useState("100");
  const [from, setFrom] = useState(defaultUnit || "MTR");
  const [to, setTo] = useState(defaultUnit === "KG" ? "MTR" : "KG");
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const options = CONVERTIBLE.filter((c) => units.length === 0 || units.some((u) => u.code === c));
  const select = "h-9 rounded-md border border-border bg-background px-2 text-sm";

  const run = async () => {
    setBusy(true);
    try {
      setResult(unwrapObject<Result>(await api.get(`/api/Product/Convert/${productId}?quantity=${encodeURIComponent(qty)}&from=${from}&to=${to}`)));
    } catch (err: any) {
      setResult({ ok: false, reason: err.message || "Could not convert" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-3.5">
      <div>
        <p className="text-xs sm:text-sm font-semibold">Unit converter</p>
        <p className="text-[11px] text-muted-foreground">Metres, yards, rolls, kilos, cones, pieces. Metres and kilos use this product's saved width and GSM.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Quantity</Label>
          <Input type="number" min={0} step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 w-28" />
        </div>
        <select value={from} onChange={(e) => setFrom(e.target.value)} className={select} aria-label="From unit">
          {options.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        <ArrowRightLeft className="size-4 text-muted-foreground mb-2.5" />
        <select value={to} onChange={(e) => setTo(e.target.value)} className={select} aria-label="To unit">
          {options.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        <Button type="button" size="sm" variant="outline" className="h-9" onClick={run} disabled={busy || qty === ""}>
          {busy ? <Loader2 className="size-3.5 animate-spin mr-1.5" /> : null}
          Convert
        </Button>
      </div>
      {result && (
        <p className={`text-sm ${result.ok ? "text-foreground" : "text-amber-700 dark:text-amber-400"}`}>
          {result.ok ? (
            <>
              <strong>
                {Number(qty).toLocaleString("en-IN")} {from} = {result.quantity?.toLocaleString("en-IN")} {to}
              </strong>
              {result.how && <span className="block text-[11px] text-muted-foreground">{result.how}</span>}
            </>
          ) : (
            result.reason
          )}
        </p>
      )}
    </div>
  );
}
